import html2canvas from 'html2canvas';
import { BackgroundType } from '@/contexts/PreferencesContext';

export class PreviewGenerator {
  private static cache = new Map<string, string>();
  private static readonly CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
  
  /**
   * Generate a preview screenshot of a background component
   */
  static async generatePreview(
    element: HTMLElement,
    backgroundType: BackgroundType,
    options: {
      width?: number;
      height?: number;
      quality?: number;
    } = {}
  ): Promise<string | null> {
    const {
      width = 300,
      height = 200,
      quality = 0.8
    } = options;

    const cacheKey = `${backgroundType}-${width}x${height}`;
    
    // Check cache first
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      // Capture the element
      const canvas = await html2canvas(element, {
        width,
        height,
        scale: 1,
        logging: false,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
      });

      // Convert to data URL
      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      
      // Cache the result
      this.setCache(cacheKey, dataUrl);
      
      return dataUrl;
    } catch (error) {
      console.warn(`Failed to generate preview for ${backgroundType}:`, error);
      return null;
    }
  }

  /**
   * Generate a preview from a video element
   */
  static generateVideoPreview(
    videoElement: HTMLVideoElement,
    backgroundType: string
  ): Promise<string | null> {
    return new Promise((resolve) => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          resolve(null);
          return;
        }

        canvas.width = 300;
        canvas.height = 200;

        // Wait for video to load
        const handleCanPlay = () => {
          videoElement.removeEventListener('canplay', handleCanPlay);
          
          // Seek to middle of video for a representative frame
          videoElement.currentTime = Math.min(videoElement.duration / 2, 3);
          
          const handleSeeked = () => {
            videoElement.removeEventListener('seeked', handleSeeked);
            
            try {
              ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
              const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
              this.setCache(`video-${backgroundType}`, dataUrl);
              resolve(dataUrl);
            } catch (error) {
              console.warn('Failed to capture video frame:', error);
              resolve(null);
            }
          };
          
          videoElement.addEventListener('seeked', handleSeeked);
        };

        if (videoElement.readyState >= 2) {
          handleCanPlay();
        } else {
          videoElement.addEventListener('canplay', handleCanPlay);
        }
      } catch (error) {
        console.warn('Failed to generate video preview:', error);
        resolve(null);
      }
    });
  }

  /**
   * Get cached preview
   */
  private static getFromCache(key: string): string | null {
    const cached = this.cache.get(key);
    if (!cached) return null;

    try {
      const data = JSON.parse(cached);
      if (Date.now() - data.timestamp > this.CACHE_EXPIRY) {
        this.cache.delete(key);
        return null;
      }
      return data.url;
    } catch {
      this.cache.delete(key);
      return null;
    }
  }

  /**
   * Set cache with timestamp
   */
  private static setCache(key: string, url: string): void {
    const data = {
      url,
      timestamp: Date.now()
    };
    this.cache.set(key, JSON.stringify(data));
  }

  /**
   * Clear all cached previews
   */
  static clearCache(): void {
    this.cache.clear();
  }

  /**
   * Generate a thumbnail of a window element
   */
  static async generateWindowThumbnail(
    element: HTMLElement,
    windowId: string,
    options: {
      width?: number;
      height?: number;
      quality?: number;
    } = {}
  ): Promise<string | null> {
    const {
      width = 80,
      height = 60,
      quality = 0.7
    } = options;

    const cacheKey = `window-${windowId}-${width}x${height}`;
    
    // Check cache first
    const cached = this.getFromCache(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      // Capture the window content area
      const canvas = await html2canvas(element, {
        width: element.scrollWidth,
        height: element.scrollHeight,
        scale: 0.5, // Reduce scale for better performance
        logging: false,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        ignoreElements: (element) => {
          // Ignore resize handles and other UI elements
          return element.classList.contains('resize-handle') ||
                 element.classList.contains('window-controls') ||
                 element.getAttribute('data-ignore-screenshot') === 'true';
        },
      });

      // Create a smaller thumbnail canvas
      const thumbnailCanvas = document.createElement('canvas');
      const ctx = thumbnailCanvas.getContext('2d');
      
      if (!ctx) return null;

      thumbnailCanvas.width = width;
      thumbnailCanvas.height = height;

      // Draw the scaled-down version
      ctx.drawImage(canvas, 0, 0, width, height);
      
      // Convert to data URL
      const dataUrl = thumbnailCanvas.toDataURL('image/jpeg', quality);
      
      // Cache the result
      this.setCache(cacheKey, dataUrl);
      
      return dataUrl;
    } catch (error) {
      console.warn(`Failed to generate window thumbnail for ${windowId}:`, error);
      return null;
    }
  }

  /**
   * Get cache size (for debugging)
   */
  static getCacheSize(): number {
    return this.cache.size;
  }
}